import { AudioManager } from "../../audio/AudioManager";
import { asset } from "../../platform/assets";

/** Shared app-wide sound instance (WebAudio synth + recorded samples). */
export const audio = new AudioManager();

// Browsers require a user gesture before audio can start.
const unlock = (): void => {
  audio.resume();
  // If the menu is showing but autoplay was locked at boot, start the groove now.
  if (!document.querySelector('.game') && !document.hidden && audio.musicEnabled) {
    audio.startMusicLoop(2, 26);
  }
};
document.addEventListener('pointerdown', unlock, { capture: true });
document.addEventListener('touchend', unlock, { capture: true });

// Load recorded samples (dice roll + goti move + button click + win cheer).
// NOTE: relative asset() paths (not `/sounds/...`) so the zip works inside
// the Y8 iframe/sub-path. Same files on Play Store — zero behavior change.
void audio.loadDiceSample(asset('sounds/dice-roll.mp3'));
void audio.loadSample('token_move', asset('sounds/goti-move.mp3'), true);
void audio.loadSample('click', asset('sounds/click.mp3'), true);
void audio.loadWinMusic(asset('sounds/win-cheer.mp3'));

// Background music: loops only the 2s–26s slice of the track.
// Menu-only — GameScreen stops it via stopMusic(). The guard below fixes
// old/slow phones where the 6MB file finishes loading AFTER the board
// opened (music used to start over the silent board).
void audio.loadMusic(asset('music/bg-music.mp3')).then(() => {
  if (!document.querySelector('.game') && !document.hidden && audio.musicEnabled) {
    audio.startMusicLoop(2, 26);
  }
});

/* ---------------- persisted audio preferences ---------------- */

export interface AudioPrefs {
  musicOn: boolean;
  musicVol: number; // 0..1
  sfxOn: boolean;
  sfxVol: number; // 0..1
}

const STORE_KEY = "nelplox-audio-v1";

export const defaultAudioPrefs: AudioPrefs = {
  musicOn: true,
  musicVol: 0.7,
  sfxOn: true,
  sfxVol: 0.9,
};

export function loadAudioPrefs(): AudioPrefs {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (!raw) return { ...defaultAudioPrefs };
    return { ...defaultAudioPrefs, ...JSON.parse(raw) };
  } catch {
    return { ...defaultAudioPrefs };
  }
}

export function saveAudioPrefs(prefs: AudioPrefs): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(prefs));
  } catch { /* storage unavailable */ }
}

/** Apply saved preferences once at startup. */
const saved = loadAudioPrefs();
audio.setMusicEnabled(saved.musicOn);
audio.setMusicVolume(saved.musicVol);
audio.setSoundEnabled(saved.sfxOn);
audio.setSfxVolume(saved.sfxVol);

// When a full-screen ad covers the app, pause menu music so it never
// plays over the ad's own audio. AdManager fires these window events on
// Showed (open) and Dismissed/FailedToShow (close) — no import cycle.
// startMatch() also pauses explicitly before show() for zero-gap ducking.
if (typeof window !== 'undefined') {
  window.addEventListener('ludo:ad-open', () => {
    try { audio.pauseForAd(); } catch { /* audio unavailable */ }
  });
  window.addEventListener('ludo:ad-close', () => {
    try {
      // Board is silent by design — only resume if we're still on a menu.
      const onBoard = !!document.querySelector('.game');
      audio.resumeAfterAd(!onBoard);
    } catch { /* ignore */ }
  });
}
